import type { ReactNode } from "react";
import { openExternalUrl } from "@/shared/openExternalUrl";

/** Blocco del pannello. ``evidenza``: bordo accento, per la cosa da seguire
 *  adesso (una sostituzione in viaggio). Testo sempre nero (Davide 23/09): la
 *  gerarchia la fanno peso e dimensione, non il grigio. */
export function Sezione({
  titolo,
  evidenza = false,
  children,
}: {
  titolo: ReactNode;
  evidenza?: boolean;
  children: ReactNode;
}) {
  return (
    <section
      className={[
        "flex flex-col gap-2 rounded-xl bg-card px-3.5 py-3",
        evidenza
          ? "border-[1.5px] border-accent ring-[3px] ring-accent-soft"
          : "border border-line",
      ].join(" ")}
    >
      <h3 className={["text-[13px] font-bold", evidenza ? "text-accent-2" : "text-ink"].join(" ")}>
        {titolo}
      </h3>
      {children}
    </section>
  );
}

/** Coppie etichetta/valore allineate su due colonne: si legge in verticale. */
export function Righe({ children }: { children: ReactNode }) {
  return (
    <dl className="grid grid-cols-[minmax(84px,auto)_1fr] items-baseline gap-x-3 gap-y-1 text-[12.5px]">
      {children}
    </dl>
  );
}

export function Riga({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-[12px] text-ink">{label}</dt>
      <dd className="flex min-w-0 flex-wrap items-center gap-1.5 font-semibold text-ink">
        {children}
      </dd>
    </>
  );
}

/** Apre il sito del corriere col codice gia' cercato. */
export function ApriTracking({ url, codice }: { url: string; codice: string }) {
  return (
    <button
      type="button"
      onClick={() => openExternalUrl(url)}
      title="Apri sul sito del corriere"
      aria-label={`Apri ${codice} sul sito del corriere`}
      className="inline-grid h-[22px] w-[22px] shrink-0 place-items-center rounded-[5px] border border-line-2 bg-card text-ink hover:border-accent hover:text-accent-2"
    >
      <svg
        className="h-3 w-3 fill-none stroke-current stroke-[1.9] [stroke-linecap:round] [stroke-linejoin:round]"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path d="M7 17L17 7M9 7h8v8" />
      </svg>
    </button>
  );
}

const TONI = {
  neutro: "bg-paper border-line-2",
  verde: "bg-green-soft border-green/30",
  giallo: "bg-amber-soft border-amber/30",
  rosso: "bg-red-soft border-red/30",
  blu: "bg-blue-soft border-blue/30",
} as const;

export type Tono = keyof typeof TONI;

export function Pill({ tono = "neutro", children }: { tono?: Tono; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-[1px] text-[11.5px] font-semibold text-ink ${TONI[tono]}`}
    >
      {children}
    </span>
  );
}

/** Riga vuota onesta: una sezione senza dati dice «niente», non sparisce. */
export function Vuoto({ children }: { children: ReactNode }) {
  return <p className="text-[12px] text-ink">{children}</p>;
}
